// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import {
  Body,
  Button,
  Column,
  Description,
  H1,
  InlineAlert,
  LgPadding,
  MdPadding,
  MdSpacing,
  ScreenWrapper,
  SecondaryPage,
  Toast,
  ToastContext,
  XsSpacing,
  XxlPadding,
  XxlSpacing,
} from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { useContext, useState } from 'react';
import { ActivityIndicator } from 'react-native';
// eslint-disable-next-line import/no-deprecated
import { fetchDeliveries } from '~shared/actions/deliveries';
// eslint-disable-next-line import/no-deprecated
import { autobillPrescription } from '~shared/actions/prescriptions';
// eslint-disable-next-line import/no-deprecated
import { fetchPrescriptionPricing } from '~shared/actions/pricing';
import { updateQuantity } from '~shared/actions/quantityChange';
import { useFetchNextAvailableDatesForCart } from '~shared/features/next-available-date/hooks/useFetchNextAvailableDatesForCart';
import { getPrescriptionByID } from '~shared/features/prescriptions/selectors/getPrescriptionByID';
import { formatQuantityToFriendlyDosageConversion, formatWrittenQuantity } from '~shared/helpers/helper';
import { useInvalidatePrescriptions, useOrderPricing } from '~shared/queries';
import { useQuantityOptions } from '~shared/queries/query-keys/quantityOptions';
import getUpdateQuantityLoading from '~shared/selectors/quantityChange/getUpdateQuantityLoading';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type QuantityOption } from '~shared/types/clients';
import { useInvalidateMedDetails } from '../../med-details';
import { QuantityChangeSelect } from './QuantityChangeSelect';

const QuantityChangeHeader = ({
  conversion,
  writtenQuantity,
}: {
  readonly conversion: string;
  readonly writtenQuantity: string;
}) => {
  return (
    <LgPadding>
      <Column center>
        <H1>Change Quantity</H1>
        <XsSpacing />
        <Body color={COLORS.TEXT_COLORS.GREY}>{`Your doctor prescribed: ${writtenQuantity}`}</Body>
        {!!conversion.length && <Body color={COLORS.TEXT_COLORS.GREY}>{conversion}</Body>}
      </Column>
    </LgPadding>
  );
};

export const QuantityChange = () => {
  const { goBack, getParam } = useNavigation<'RouteChangeQuantity'>();
  const prescriptionID = getParam('prescriptionID');
  const { addToast } = useContext(ToastContext);
  const dispatch = useDispatchShared();
  const { invalidateMedicationQuery } = useInvalidateMedDetails();
  const { refetchOrderPricing } = useOrderPricing();
  const { invalidatePrescriptionsQuery } = useInvalidatePrescriptions();
  const prescription = useSelectorShared((state) => getPrescriptionByID(state, prescriptionID));
  const writtenQuantity = prescription ? formatWrittenQuantity(prescription) : '';
  const conversion = prescription ? formatQuantityToFriendlyDosageConversion(prescription) : '';
  const { options, isLoading: quantityOptionsLoading } = useQuantityOptions(prescriptionID);
  const friendlyDosageForm = prescription?.product_friendly_dosage_form || prescription?.dosage_form || '';
  const updateQuantityLoading = useSelectorShared(getUpdateQuantityLoading);
  const fetchNextAvailableDatesForCart = useFetchNextAvailableDatesForCart();
  const [selectedOption, setSelectedOption] = useState<QuantityOption | undefined>(
    options.find((option) => option.quantity === prescription?.quantity),
  );

  if (quantityOptionsLoading) {
    return (
      <ScreenWrapper>
        <QuantityChangeHeader
          conversion={conversion}
          writtenQuantity={writtenQuantity}
        />
        <XxlPadding>
          <Column centerHorizontally>
            <ActivityIndicator
              testID="loading-indicator"
              accessibilityHint="loading"
              animating
              color={COLORS.TEXT_COLORS.GREY}
              size="large"
            />
          </Column>
        </XxlPadding>
      </ScreenWrapper>
    );
  }

  const handleQuantityChange = async () => {
    if (!selectedOption) return;
    const newQuantity = selectedOption.quantity;

    try {
      const success = await dispatch(updateQuantity(prescriptionID, newQuantity));
      if (!success) {
        addToast(<Toast variant="error">Unable to update quantity. Please try again or message us for support.</Toast>);
        return;
      }

      // Refresh the data that drives the displayed quantity on med details (the prescriptions list and
      // the med-details medication query) and wait for the refetch BEFORE navigating back, so med details
      // renders with the new quantity instead of briefly flashing the stale one. The quantity was already
      // persisted server-side, so swallow any refetch rejection to guarantee we still navigate back.
      await Promise.all([invalidatePrescriptionsQuery(), invalidateMedicationQuery(prescriptionID)]).catch(() => {});
      goBack();

      // The remaining refreshes don't affect the displayed quantity, so they can run after navigation.
      // eslint-disable-next-line import/no-deprecated
      await dispatch(autobillPrescription(prescriptionID));
      refetchOrderPricing();
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchPrescriptionPricing());
      // eslint-disable-next-line import/no-deprecated
      dispatch(fetchDeliveries());
      fetchNextAvailableDatesForCart({ unbundledPrescriptionIDs: [prescriptionID] });
    } catch (error) {
      console.error(error);
    }
  };

  const hasQuantityChanged = !!prescription?.quantity && selectedOption?.quantity !== prescription?.quantity;

  return (
    <SecondaryPage
      HeaderContent={
        <QuantityChangeHeader
          writtenQuantity={writtenQuantity}
          conversion={conversion}
        />
      }
    >
      <XxlSpacing />
      <QuantityChangeSelect
        onChange={setSelectedOption}
        options={options}
        friendlyDosageForm={friendlyDosageForm}
        selectedOption={selectedOption}
      />
      {hasQuantityChanged ? (
        <>
          <MdSpacing />
          <InlineAlert type="neutral">
            <Description>
              Please check with your provider before changing quantity. Once you confirm, an updated price may not be
              available immediately. Finding the best price for your medication can take up to an hour but is usually
              fewer than 10 minutes.
            </Description>
          </InlineAlert>
        </>
      ) : null}
      <MdPadding>
        <Button
          width="full"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onPress={handleQuantityChange}
          disabled={!hasQuantityChanged}
          label="Confirm quantity change"
          loading={updateQuantityLoading}
        />
        <XsSpacing />
        <Button
          width="full"
          type="tertiary"
          onPress={goBack}
          label="Cancel"
        />
      </MdPadding>
    </SecondaryPage>
  );
};
